import {
  addInAppNotification,
  markAsRead,
  incrementUnreadCount,
  decrementUnreadCount,
  setOrderConfirmationModal,
  clearOrderConfirmationModal,
  clearAllNotifications,
  InAppNotification
} from '../../Redux/Slices/notificationSlice';
import { FCMApiService } from './FCMApiService';
import { getStoreRef } from './storeRef';
import { storeActiveStoreId } from '../../utils/indexedDBUtils';
import {
  getSellerOrgBuildConfig,
  resolveNotificationIconUrl,
} from '../../config/sellerOrgConfig';
import { NOTIFICATION_SOUND_URL } from '../../config/notificationSound';
import {
  isGlobalOrderNotificationRole,
  isUserNotificationRole,
} from '../../utils/notificationRoleUtils';

export interface OrderConfirmationData {
  orderId: string;
  customerName: string;
  items: any[];
  total: number;
  status: string;
  timestamp: string;
  orderDetails?: any;
}


// Resolves the Redux store lazily via a bridge module to avoid the
// circular type dependency between this service and the Redux store.
function getStore(): any {
  return getStoreRef();
}

/** Check if the user has notification.view permission */
function hasNotificationViewPermission(): boolean {
  const state = getStore()?.getState();
  const roleID = state?.roleAndPermission?.roleID || 0;
  const roleName = state?.roleAndPermission?.name || '';
  const notificationScope = state?.roleAndPermission?.notification_scope;
  const hasUserNotificationRole = isUserNotificationRole(roleName, notificationScope);
  const permissions = state?.roleAndPermission?.permissions;
  const explicitPermission = permissions?.notification?.view;

  if (hasUserNotificationRole) {
    return true;
  }

  if (explicitPermission === true) {
    return true;
  }
  if (explicitPermission === false) {
    return false;
  }

  // Fallback: during initial permission hydration, allow for store roles
  // that are FCM-eligible so we don't drop live order alerts.
  return roleID === 3 || roleID === 4;
}

/** Check if the current user's role is enabled for order notifications */
function isRoleEnabledForNotifications(): boolean {
  const state = getStore().getState();
  const roleID = state.roleAndPermission?.roleID || 0;
  const roleName = state?.roleAndPermission?.name || '';
  const notificationScope = state?.roleAndPermission?.notification_scope;
  const hasUserNotificationRole = isUserNotificationRole(roleName, notificationScope);

  if (hasUserNotificationRole) {
    return true;
  }

  const enabledRoleIds = state.orderNotificationSettings?.enabledRoleIds ?? [3, 4];
  return enabledRoleIds.includes(roleID);
}

function shouldBypassStoreNotificationFilter(): boolean {
  const state = getStore().getState();
  const roleName = state?.roleAndPermission?.name || '';
  return isGlobalOrderNotificationRole(roleName);
}

/** Check if the current user has sound enabled via the speaker toggle */
function isSoundEnabledForUser(): boolean {
  // If notification.view permission is false, sound is disabled
  if (!hasNotificationViewPermission()) {
    return false;
  }
  const state = getStore().getState();
  const roleID = state.roleAndPermission?.roleID || 0;

  const userKey = `role_${roleID}`;
  // Sound is ON by default for eligible notification users, but the speaker
  // toggle can mute it without blocking drawer/browser notifications.
  return state.orderNotificationSettings?.soundEnabledByUser?.[userKey] ?? true;
}

export class NotificationService {
  private fcmApiService: FCMApiService;
  private audioContext: AudioContext | null = null;
  private notificationSound: AudioBuffer | null = null;
  private notificationSoundSource: AudioBufferSourceNode | null = null;
  private orderQueue: any[] = [];
  private isProcessingQueue = false;
  private continuousSoundInterval: NodeJS.Timeout | null = null;
  private continuousSoundSource: AudioBufferSourceNode | null = null;
  private isPlayingContinuousSound = false;
  private globalOrderSoundInterval: NodeJS.Timeout | null = null;
  private globalOrderSoundSource: AudioBufferSourceNode | null = null;
  private isPlayingGlobalOrderSound = false;
  private globalOrderSoundTimeout: NodeJS.Timeout | null = null; // Track 30-second timeout
  private audioInitialized = false;
  private audioInitializationAttempted = false;
  private orderActionChannel: BroadcastChannel | null = null;
  private lastSoundError: string | null = null;
  private lastSoundPlayedAt: string | null = null;

  private isAutoplayPolicyBlock(reason: string): boolean {
    const normalizedReason = reason.toLowerCase();
    return (
      normalizedReason.includes('notallowed') ||
      normalizedReason.includes('not allowed') ||
      normalizedReason.includes('user gesture') ||
      normalizedReason.includes('interact')
    );
  }

  private notifyBrowserSoundBlocked(reason: string): void {
    if (this.isAutoplayPolicyBlock(reason)) {
      console.warn('[NotificationService] Audio needs a user gesture; keeping sound preference ON without persistent browser-block prompt.');
      return;
    }

    try {
      localStorage.setItem('browser_sound_blocked', 'true');
    } catch (_) {
      // ignore storage failures
    }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('browser-sound-blocked', { detail: { reason } }),
      );
    }
  }

  private clearBrowserSoundBlocked(): void {
    try {
      localStorage.removeItem('browser_sound_blocked');
    } catch (_) {
      // ignore storage failures
    }
  }

  private async playHtmlAudioFallback(): Promise<void> {
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.volume = 1;
    audio.preload = 'auto';
    await audio.play();
    this.lastSoundError = null;
    this.lastSoundPlayedAt = new Date().toISOString();
    console.log('[NotificationService] Notification sound played with HTMLAudio fallback');
  }

  /**
   * Fallback: Show a native browser notification with OS sound when AudioContext is not available.
   * The OS plays the notification sound — no user gesture required.
   * Only needs Notification.permission === 'granted' (already required for FCM).
   * Auto-closes after 5 seconds to avoid clutter.
   */
  showNativeNotificationWithSound(orderData?: any): void {
    try {
      // Only show when tab is in foreground — for background tabs, the service worker
      // already shows a native notification with sound. Prevents duplicate notifications.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        console.log('[NotificationService] Tab is in background, service worker handles notification sound');
        return;
      }

      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        console.warn('[NotificationService] Cannot show native notification - permission not granted');
        return;
      }

      const currentOrder = orderData || this.orderQueue[0] || {};
      const orderNumber =
        currentOrder?.order_number ||
        currentOrder?.order_code ||
        currentOrder?.orderCode ||
        currentOrder?.order_id ||
        currentOrder?.orderId ||
        currentOrder?.id ||
        '';
      const storeName =
        currentOrder?.store_name ||
        currentOrder?.storeName ||
        currentOrder?.store_data?.name ||
        currentOrder?.storeData?.name ||
        currentOrder?.store?.name ||
        '';
      const status = typeof currentOrder?.status === 'string' ? currentOrder.status.toLowerCase() : '';
      const statusLabel =
        status === 'cancelled'
          ? 'Order Cancelled'
          : status === 'billed'
            ? 'Order Accepted'
            : 'New Order';
      const body = `${orderNumber ? `#${orderNumber}` : 'New order'}${storeName ? ` - ${storeName}` : ''}`;

      const iconUrl = resolveNotificationIconUrl(getSellerOrgBuildConfig().branding);
      const notification = new Notification(statusLabel, {
        body,
        ...(iconUrl ? { icon: iconUrl } : {}),
        silent: false, // OS will play its notification sound
        tag: `order-${orderNumber || Date.now()}-${status || 'new'}`, // Prevent duplicate notifications for same order/status
        requireInteraction: false,
      });

      // Auto-close after 5 seconds — seller already has the in-app popup
      setTimeout(() => {
        try { notification.close(); } catch (_) { /* ignore */ }
      }, 5000);

      console.log('[NotificationService] Native notification shown with OS sound for order:', orderNumber);
    } catch (error) {
      console.warn('[NotificationService] Failed to show native notification:', error);
    }
  }

  constructor() {
    this.fcmApiService = new FCMApiService();

    // Make queue status accessible globally for debugging
    (window as any).getOrderQueueStatus = () => this.logQueueStatus();
    (window as any).getQueueLength = () => this.orderQueue.length;
    (window as any).getQueueContents = () => this.orderQueue.map(order => order.id || order.orderId);
    (window as any).initializeAudio = () => this.initializeAudio();
    (window as any).getAudioStatus = () => this.getAudioStatus();
    (window as any).notificationSoundStatus = () => this.getAudioStatus();
    (window as any).testNotificationSound = async () => {
      await this.tryInitializeAudio();
      await this.playNotificationSound();
      return this.getAudioStatus();
    };
    (window as any).isAudioReady = () => this.isAudioReady();
    (window as any).getCurrentStatus = () => this.getCurrentStatus();
    (window as any).manualStartSound = () => this.manualStartSound();
    (window as any).manualStopSound = () => this.manualStopSound();
    (window as any).forceRestartSound = () => this.forceRestartSound();
    (window as any).setStoreId = (storeId: number) => this.setStoreId(storeId);
    (window as any).showAudioInitModal = () => {
      // Dispatch event to show audio init modal
      window.dispatchEvent(new CustomEvent('show-audio-init-modal'));
    };

    // Set up user gesture listeners for audio initialization
    this.setupUserGestureListeners();

    // Older builds stored this flag for normal autoplay failures. That made the
    // app ask to enable sound after every reload even when the user preference
    // was still ON.
    this.clearBrowserSoundBlocked();

    // Re-check audio when tab becomes visible (e.g. after sleep or next day)
    this.setupVisibilityListener();

    // Set up cross-tab sync so sound stops in ALL tabs when order is actioned
    this.setupCrossTabSync();

    // Start continuous queue monitoring
    this.startQueueMonitoring();
  }

  /** When tab becomes visible, try to resume suspended AudioContext so sound CAN work
   *  for future new orders. Does NOT restart sound — sound only plays when new orders arrive.
   *  If the AudioContext was closed/garbage-collected (Edge, some browsers after tab discard),
   *  re-initialize it from scratch so audio is READY for the next new order. */
  private setupVisibilityListener(): void {
    if (typeof document === 'undefined' || !document.addEventListener) return;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;

      // AudioContext was closed or garbage-collected (e.g. Edge discarded the tab)
      if (this.audioInitialized && (!this.audioContext || this.audioContext.state === 'closed')) {
        console.log('[NotificationService] AudioContext was closed/discarded, re-initializing...');
        this.audioInitialized = false;
        this.audioInitializationAttempted = false;
        this.audioContext = null;
        this.notificationSound = null;
        // Re-initialize audio so it's READY for future new orders (do NOT restart sound)
        this.initializeAudio().catch(err => {
          console.warn('[NotificationService] Failed to re-initialize audio on tab visible:', err);
        });
        return;
      }

      if (!this.audioContext || !this.audioInitialized) return;
      if (this.audioContext.state !== 'suspended') return;
      // Resume AudioContext so it's ready for future new orders (do NOT restart sound)
      this.audioContext.resume().then(() => {
        console.log('[NotificationService] AudioContext resumed on tab visible');
      }).catch((err) => {
        console.warn('[NotificationService] Could not resume AudioContext on visible:', err);
      });
    });
  }

  /** Cross-tab sync: when an order is accepted/rejected/closed in one tab,
   *  broadcast to all other tabs so they stop sound and remove the order.
   *  Uses BroadcastChannel (Chrome/Edge/Firefox) with localStorage fallback (Safari). */
  private setupCrossTabSync(): void {
    // Try BroadcastChannel first (Chrome 54+, Edge 79+, Firefox 38+, Safari 15.4+)
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.orderActionChannel = new BroadcastChannel('order-notification-sync');
        this.orderActionChannel.onmessage = (event) => {
          this.handleCrossTabMessage(event.data);
        };
        return;
      } catch (err) {
        console.warn('[NotificationService] BroadcastChannel failed, using localStorage fallback:', err);
      }
    }

    // Fallback: localStorage storage event (works on all browsers including older Safari)
    // The 'storage' event fires in OTHER tabs when localStorage changes — exactly what we need
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key === 'order-action-sync' && event.newValue) {
          try {
            this.handleCrossTabMessage(JSON.parse(event.newValue));
          } catch (_) { /* ignore parse errors */ }
        }
      });
    }
  }

  /** Handle a cross-tab message (from BroadcastChannel or localStorage fallback). */
  private handleCrossTabMessage(data: any): void {
    const { type, orderId } = data || {};
    if (type === 'ORDER_ACTIONED' && orderId) {
      console.log('[NotificationService] Cross-tab sync: order actioned in another tab:', orderId);
      this.removeOrderFromQueue(orderId);
      // Stop all sounds — do NOT restart. Sound only plays once when new orders first arrive.
      this.stopAllSounds();
    }
  }

  /** Notify other tabs that an order was accepted/rejected/closed. */
  private broadcastOrderAction(orderId: string | number): void {
    const message = { type: 'ORDER_ACTIONED', orderId: orderId?.toString() };
    try {
      if (this.orderActionChannel) {
        this.orderActionChannel.postMessage(message);
      } else if (typeof localStorage !== 'undefined') {
        // localStorage fallback: add timestamp to ensure 'storage' event fires even for same orderId
        localStorage.setItem('order-action-sync', JSON.stringify({ ...message, ts: Date.now() }));
      }
    } catch (_) {
      // ignore — channel may be closed or localStorage unavailable
    }
  }

  private setupUserGestureListeners(): void {
    const userGestureEvents = ['click', 'touchstart', 'keydown', 'mousedown'];

    const initializeAudioOnUserGesture = async () => {
      // Already initialized — remove all listeners and stop
      if (this.audioInitialized) {
        userGestureEvents.forEach(event => {
          document.removeEventListener(event, initializeAudioOnUserGesture);
        });
        return;
      }

      // Another attempt is already in progress — skip this event
      if (this.audioInitializationAttempted) return;

      this.audioInitializationAttempted = true;
      try {
        await this.initializeAudio();

        if (this.audioInitialized) {
          // Success — remove all listeners
          userGestureEvents.forEach(event => {
            document.removeEventListener(event, initializeAudioOnUserGesture);
          });
          console.log('[NotificationService] Audio initialized silently via user gesture');

          // If orders arrived before audio was ready, start sound now
          if (this.orderQueue.length > 0 && !this.isPlayingGlobalOrderSound) {
            console.log('[NotificationService] Orders waiting in queue, starting sound after audio init');
            this.startGlobalOrderSound();
          }
        }
      } catch (error) {
        console.error('Failed to initialize audio on user gesture:', error);
      } finally {
        // Always reset so the next user gesture can retry
        if (!this.audioInitialized) {
          this.audioInitializationAttempted = false;
        }
      }
    };

    // Do NOT use { once: true } — keep listening until audio is successfully initialized
    userGestureEvents.forEach(event => {
      document.addEventListener(event, initializeAudioOnUserGesture);
    });
  }

  private async initializeAudio(): Promise<void> {
    if (this.audioInitialized) {
      return;
    }

    try {
      // ✅ ENHANCED: Check browser support
      if (!window.AudioContext && !(window as any).webkitAudioContext) {
        throw new Error('Web Audio API not supported in this browser');
      }

      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log('[NotificationService] AudioContext created, state:', this.audioContext.state);

      // ✅ ENHANCED: Try to resume if suspended
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
          console.log('[NotificationService] AudioContext resumed, new state:', this.audioContext.state);
        } catch (resumeError) {
          console.warn('[NotificationService] Failed to resume audio context:', resumeError);
          // Continue anyway - might work on next user interaction
        }
      }

      const response = await fetch(NOTIFICATION_SOUND_URL);
      if (!response.ok) {
        throw new Error(`Failed to load sound file from ${NOTIFICATION_SOUND_URL}: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      console.log('[NotificationService] Sound file downloaded, size:', arrayBuffer.byteLength, 'bytes');
      
      this.notificationSound = await this.audioContext.decodeAudioData(arrayBuffer);
      console.log('[NotificationService] Sound buffer decoded successfully');
      
      this.audioInitialized = true;
      // Clear the browser_sound_blocked flag — audio is working now.
      // This prevents the BravePushSettingsModal from showing on next page load.
      this.clearBrowserSoundBlocked();
      console.log('[NotificationService] ✅ Audio initialized successfully');
    } catch (error) {
      console.error('[NotificationService] ❌ Failed to initialize audio:', error);
      console.error('[NotificationService] Error details:', {
        errorMessage: error instanceof Error ? error.message : String(error),
        audioContext: !!this.audioContext,
        audioContextState: this.audioContext?.state
      });
      this.audioInitializationAttempted = false;
      // Don't throw - allow retry on next user interaction
    }
  }

  private async ensureAudioInitialized(): Promise<void> {
    if (!this.audioInitialized) {
      console.log('Audio not initialized, waiting for user gesture...');
      return;
    }
  }

  async playNotificationSound(): Promise<void> {
    if (!isSoundEnabledForUser()) {
      console.log('Sound disabled by user toggle; skipping notification sound');
      return;
    }

    if (!this.audioInitialized) {
      console.log('Audio not initialized - sound will play after user interaction');
      try {
        await this.initializeAudio();
      } catch (initError) {
        const msg = initError instanceof Error ? initError.message : String(initError);
        this.lastSoundError = msg;
        console.warn('[NotificationService] Audio initialization before play failed:', initError);
      }
    }

    if (!this.audioInitialized) {
      try {
        await this.playHtmlAudioFallback();
      } catch (fallbackError) {
        const fallbackMsg = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
        this.lastSoundError = fallbackMsg;
        console.warn('[NotificationService] HTMLAudio fallback failed:', fallbackError);
      }
      return;
    }

    try {
      if (this.audioContext && this.notificationSound) {
        if (this.audioContext.state === 'suspended') {
          try {
            await this.audioContext.resume();
          } catch (resumeError) {
            console.warn('Failed to resume audio context for notification sound:', resumeError);
          }
        }

        // Stop any previous notification sound source to avoid overlap
        if (this.notificationSoundSource) {
          try {
            this.notificationSoundSource.stop();
          } catch {
            // ignore
          }
          this.notificationSoundSource = null;
        }

        const source = this.audioContext.createBufferSource();
        const gainNode = this.audioContext.createGain();
        source.buffer = this.notificationSound;
        gainNode.gain.value = 3.5;
        source.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        source.start();

        this.notificationSoundSource = source;

        source.onended = () => {
          if (this.notificationSoundSource === source) {
            this.notificationSoundSource = null;
          }
        };

        this.clearBrowserSoundBlocked();
        this.lastSoundError = null;
        this.lastSoundPlayedAt = new Date().toISOString();
        console.log('Notification sound played');
      } else {
        console.warn('Audio not initialized or sound not loaded');
        await this.playHtmlAudioFallback();
      }
    } catch (error) {
      console.error('Failed to play notification sound:', error);
      const msg = error instanceof Error ? error.message : String(error);
      this.lastSoundError = msg;
      try {
        await this.playHtmlAudioFallback();
      } catch (fallbackError) {
        const fallbackMsg = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
        this.lastSoundError = fallbackMsg;
        console.warn('[NotificationService] HTMLAudio fallback failed:', fallbackError);
      }
      if (msg.toLowerCase().includes('notallowed') || msg.toLowerCase().includes('not allowed')) {
        this.notifyBrowserSoundBlocked(msg);
      }
    }
  }

  playOrderSound(): void {
    if (!isSoundEnabledForUser()) {
      console.log('Sound disabled by user toggle — skipping order sound');
      return;
    }

    if (!this.audioInitialized) {
      console.log('Audio not initialized - sound will play after user interaction');
      return;
    }

    try {
      if (this.audioContext && this.notificationSound) {
        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume();
        }

        const playLoop = () => {
          const source = this.audioContext!.createBufferSource();
          const gainNode = this.audioContext!.createGain();
          source.buffer = this.notificationSound!;
          gainNode.gain.value = 2.5;
          source.connect(gainNode);
          gainNode.connect(this.audioContext!.destination);
          source.start();

          setTimeout(() => {
            source.stop();
          }, 3000);
        };

        playLoop();
        setTimeout(playLoop, 2000);
        console.log('Order sound played');
      } else {
        console.warn('Audio not initialized or sound not loaded for order sound');
      }
    } catch (error) {
      console.error('Failed to play order sound:', error);
    }
  }

  startContinuousOrderSound(): void {
    if (this.isPlayingContinuousSound) {
      return;
    }

    if (!isSoundEnabledForUser()) {
      console.log('Sound disabled by user toggle — skipping continuous sound');
      return;
    }

    if (!this.audioInitialized) {
      console.log('Audio not initialized - continuous sound will start after user interaction');
      return;
    }

    try {
      if (this.audioContext && this.notificationSound) {
        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume();
        }

        this.isPlayingContinuousSound = true;

        const playSound = () => {
          if (!this.isPlayingContinuousSound) return;

          // Stop any previous continuous sound source immediately
          if (this.continuousSoundSource) {
            try {
              this.continuousSoundSource.stop();
            } catch {
              // ignore
            }
            this.continuousSoundSource = null;
          }

          const source = this.audioContext!.createBufferSource();
          const gainNode = this.audioContext!.createGain();
          source.buffer = this.notificationSound!;
          gainNode.gain.value = 2.5;
          source.connect(gainNode);
          gainNode.connect(this.audioContext!.destination);
          source.start();

          this.continuousSoundSource = source;

          setTimeout(() => {
            try {
              source.stop();
            } catch {
              // ignore
            }
            if (this.continuousSoundSource === source) {
              this.continuousSoundSource = null;
            }
          }, 2000);
        };

        playSound();
        this.continuousSoundInterval = setInterval(playSound, 3000);

        console.log('Continuous order sound started');
      } else {
        console.warn('Audio not initialized or sound not loaded');
      }
    } catch (error) {
      console.error('Failed to start continuous order sound:', error);
    }
  }

  stopContinuousOrderSound(): void {
    if (this.continuousSoundInterval) {
      clearInterval(this.continuousSoundInterval);
      this.continuousSoundInterval = null;
    }
    if (this.continuousSoundSource) {
      try {
        this.continuousSoundSource.stop();
      } catch {
        // ignore
      }
      this.continuousSoundSource = null;
    }
    this.isPlayingContinuousSound = false;
    console.log('Continuous order sound stopped');
  }

  startGlobalOrderSound(): void {
    if (this.isPlayingGlobalOrderSound) {
      console.log('[NotificationService] Global order sound already playing, skipping duplicate start');
      return;
    }

    // Set flag IMMEDIATELY to prevent race conditions when multiple orders arrive simultaneously.
    // Each async path (audio init, AudioContext resume) retries startGlobalOrderSound —
    // without this early flag, multiple calls could pass the guard above before any sets it.
    this.isPlayingGlobalOrderSound = true;

    // Defensive: clean up any leaked intervals/sources from previous calls
    if (this.globalOrderSoundInterval) {
      clearInterval(this.globalOrderSoundInterval);
      this.globalOrderSoundInterval = null;
    }
    if (this.globalOrderSoundSource) {
      try { this.globalOrderSoundSource.stop(); } catch { /* ignore */ }
      this.globalOrderSoundSource = null;
    }

    if (!isSoundEnabledForUser()) {
      console.log('Sound disabled by user toggle — skipping global order sound');
      this.isPlayingGlobalOrderSound = false;
      return;
    }

    // Try to initialize audio if not initialized (may work if popup triggered user interaction)
    if (!this.audioInitialized) {
      console.log('[NotificationService] Audio not initialized, attempting to initialize...');
      this.isPlayingGlobalOrderSound = false; // Reset — async init will call startGlobalOrderSound again
      this.initializeAudio().then(() => {
        if (this.audioInitialized) {
          console.log('[NotificationService] Audio initialized successfully, starting sound...');
          this.startGlobalOrderSound();
        } else {
          console.warn('[NotificationService] Audio initialization failed - using native notification sound as fallback');
          this.showNativeNotificationWithSound();
        }
      }).catch(() => {
        console.warn('[NotificationService] Failed to initialize audio - using native notification sound as fallback');
        this.showNativeNotificationWithSound();
      });
      return;
    }

    // Guard: audioContext or sound buffer missing after init
    if (!this.audioContext || !this.notificationSound) {
      console.log('[NotificationService] Audio not ready, cannot play sound');
      this.isPlayingGlobalOrderSound = false;
      return;
    }

    try {
      if (this.audioContext && this.notificationSound) {
        // Ensure AudioContext is resumed before playing
        if (this.audioContext.state === 'suspended') {
          console.log('[NotificationService] AudioContext is suspended, attempting to resume...');
          this.isPlayingGlobalOrderSound = false; // Reset — resume will call startGlobalOrderSound again
          this.audioContext.resume().then(() => {
            console.log('[NotificationService] AudioContext resumed successfully');
            if (!this.isPlayingGlobalOrderSound) {
              this.startGlobalOrderSound();
            }
          }).catch(err => {
            console.warn('[NotificationService] Could not resume AudioContext:', err);
          });
          return;
        }

        const playSound = () => {
          if (!this.isPlayingGlobalOrderSound) {
            return;
          }

          // Guard: AudioContext may have been closed/GC'd (Edge tab discard, browser sleep)
          if (!this.audioContext || this.audioContext.state === 'closed') {
            console.warn('[NotificationService] AudioContext lost during playback, stopping sound');
            this.stopGlobalOrderSound();
            return;
          }

          // Stop any previous global sound source immediately
          if (this.globalOrderSoundSource) {
            try {
              this.globalOrderSoundSource.stop();
            } catch {
              // ignore
            }
            this.globalOrderSoundSource = null;
          }

          try {
            const source = this.audioContext.createBufferSource();
            const gainNode = this.audioContext.createGain();
            source.buffer = this.notificationSound!;
            gainNode.gain.value = 2.5;
            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);
            source.start();

            this.globalOrderSoundSource = source;

            setTimeout(() => {
              try {
                source.stop();
              } catch {
                // ignore
              }
              if (this.globalOrderSoundSource === source) {
                this.globalOrderSoundSource = null;
              }
            }, 2000);
          } catch (err) {
            console.warn('[NotificationService] Error in playSound tick, stopping:', err);
            this.stopGlobalOrderSound();
          }
        };

        playSound();
        this.globalOrderSoundInterval = setInterval(playSound, 3000);

        // Set 30-second timeout to automatically stop sound
        // Clear any existing timeout first (in case sound was restarted)
        if (this.globalOrderSoundTimeout) {
          clearTimeout(this.globalOrderSoundTimeout);
          this.globalOrderSoundTimeout = null;
        }

        this.globalOrderSoundTimeout = setTimeout(() => {
          console.log('[NotificationService] 30-second timeout reached, stopping sound automatically');
          this.stopGlobalOrderSound();
        }, 30000); // 30 seconds

        this.clearBrowserSoundBlocked();
        console.log('[NotificationService] ✅ Global order sound started (will auto-stop after 30 seconds)');
      } else {
        console.warn('[NotificationService] Audio not ready for playback');
      }
    } catch (error) {
      console.error('[NotificationService] Failed to start global order sound:', error);
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.toLowerCase().includes('notallowed') || msg.toLowerCase().includes('not allowed')) {
        this.notifyBrowserSoundBlocked(msg);
      }
    }
  }

  stopGlobalOrderSound(): void {
    // Clear the 30-second timeout
    if (this.globalOrderSoundTimeout) {
      clearTimeout(this.globalOrderSoundTimeout);
      this.globalOrderSoundTimeout = null;
    }

    if (this.globalOrderSoundInterval) {
      clearInterval(this.globalOrderSoundInterval);
      this.globalOrderSoundInterval = null;
    }
    if (this.globalOrderSoundSource) {
      try {
        this.globalOrderSoundSource.stop();
      } catch {
        // ignore
      }
      this.globalOrderSoundSource = null;
    }
    this.isPlayingGlobalOrderSound = false;
    console.log('Global order sound stopped');
  }

  /** Stop ALL sound sources and intervals — belt-and-suspenders cleanup.
   *  Call this whenever popup is dismissed or order is accepted/rejected. */
  stopAllSounds(): void {
    this.stopGlobalOrderSound();
    this.stopContinuousOrderSound();
    if (this.notificationSoundSource) {
      try { this.notificationSoundSource.stop(); } catch { /* ignore */ }
      this.notificationSoundSource = null;
    }
    console.log('[NotificationService] All sounds stopped');
  }

  addNotification(notification: InAppNotification): void {
    getStore().dispatch(addInAppNotification(notification));
    getStore().dispatch(incrementUnreadCount());

    this.playNotificationSound().catch(error => {
      console.error('Failed to play notification sound:', error);
    });
  }

  markNotificationAsRead(notificationId: string): void {
    getStore().dispatch(markAsRead(notificationId));
    getStore().dispatch(decrementUnreadCount());

    this.fcmApiService.markAsRead(notificationId);
  }

  async fetchNotificationHistory(page: number = 1, limit: number = 10): Promise<void> {
    try {
      const notifications = await this.fcmApiService.getNotificationHistory(page, limit);

      notifications.forEach((notification: any) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _inAppNotification: InAppNotification = {
          id: notification.id,
          title: notification.title,
          body: notification.body,
          type: notification.type,
          data: notification.data,
          timestamp: notification.createdAt,
          read: notification.read,
        };
      });
    } catch (error) {
      console.error('Failed to fetch notification history:', error);
    }
  }

  showOrderConfirmationModal(orderData: any): void {
    // Check notification.view permission first
    if (!hasNotificationViewPermission()) {
      console.log('notification.view permission is false — skipping notification');
      return;
    }

    if (!isRoleEnabledForNotifications()) {
      console.log('Role not enabled for order notifications — skipping');
      return;
    }

    // ✅ CRITICAL: Ensure ACTIVE_STORE_ID is set from CURRENT_STORE_ID if missing
    // This handles cases where ACTIVE_STORE_ID might not be initialized yet
    const currentActiveStoreId = localStorage.getItem('ACTIVE_STORE_ID');
    const currentStoreId = localStorage.getItem('CURRENT_STORE_ID');
    if (!currentActiveStoreId && currentStoreId) {
      console.log('[NotificationService] ⚠️ ACTIVE_STORE_ID not set, setting from CURRENT_STORE_ID:', currentStoreId);
      localStorage.setItem('ACTIVE_STORE_ID', currentStoreId);
      // Sync to IndexedDB immediately
      storeActiveStoreId(currentStoreId).catch(err => {
        console.error('[NotificationService] Failed to sync ACTIVE_STORE_ID to IndexedDB:', err);
      });
    }

    // ✅ ENHANCED: Check if order is for the active store (for sellers with multiple stores)
    // For multi-store sellers, use ACTIVE_STORE_ID to filter notifications
    // This ensures notifications (popup and sound) only trigger for the active store
    
    // ✅ ENHANCED: Check multiple possible locations for store_id in order data
    // Backend sends store_id as STRING in FCM data payload, so handle both string and number
    const orderStoreIdRaw = orderData?.store_id || 
                            orderData?.storeId || 
                            orderData?.store?.id || 
                            orderData?.store_data?.id || 
                            null;
    
    // Convert to number with proper validation (handle both string "475" and number 475)
    // Use parseInt with radix 10 to avoid NaN issues, then validate
    let orderStoreId: number | null = null;
    if (orderStoreIdRaw !== null && orderStoreIdRaw !== undefined && orderStoreIdRaw !== '') {
      const parsed = typeof orderStoreIdRaw === 'string' 
        ? parseInt(orderStoreIdRaw, 10) 
        : Number(orderStoreIdRaw);
      orderStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
    }
    
    // ✅ PRIORITY: Use ACTIVE_STORE_ID for notification filtering (multi-store sellers)
    // Fallback to CURRENT_STORE_ID for backward compatibility
    // Handle both string and number formats from localStorage
    const activeStoreIdRaw = localStorage.getItem('ACTIVE_STORE_ID') || 
                             localStorage.getItem('CURRENT_STORE_ID') || 
                             localStorage.getItem('storeId') || 
                             null;
    
    let activeStoreId: number | null = null;
    if (activeStoreIdRaw !== null && activeStoreIdRaw !== undefined && activeStoreIdRaw !== '') {
      const parsed = parseInt(activeStoreIdRaw, 10);
      activeStoreId = !isNaN(parsed) && isFinite(parsed) ? parsed : null;
    }
    
    console.log('[NotificationService] 🔍 Store check - Order data object:', orderData);
    console.log('[NotificationService] 🔍 Store check - Order data keys:', orderData ? Object.keys(orderData) : 'no orderData');
    console.log('[NotificationService] 🔍 Store check - Raw order store ID:', orderStoreIdRaw, '(type:', typeof orderStoreIdRaw, ')');
    console.log('[NotificationService] 🔍 Store check - Order store ID (converted):', orderStoreId);
    console.log('[NotificationService] 🔍 Store check - Raw active store ID:', activeStoreIdRaw, '(type:', typeof activeStoreIdRaw, ')');
    console.log('[NotificationService] 🔍 Store check - Active store ID (converted):', activeStoreId);
    
    // ✅ ENHANCED: Also compare as strings for extra safety (backend sends as string)
    const orderStoreIdStr = orderStoreIdRaw ? String(orderStoreIdRaw).trim() : null;
    const activeStoreIdStr = activeStoreIdRaw ? String(activeStoreIdRaw).trim() : null;
    const stringMatch = orderStoreIdStr && activeStoreIdStr && orderStoreIdStr === activeStoreIdStr;
    const numberMatch = orderStoreId !== null && activeStoreId !== null && orderStoreId === activeStoreId;
    
    console.log('[NotificationService] 🔍 Store check - String comparison:', {
      orderStoreIdStr,
      activeStoreIdStr,
      stringMatch
    });
    console.log('[NotificationService] 🔍 Store check - Number comparison:', {
      orderStoreId,
      activeStoreId,
      numberMatch
    });
    console.log('[NotificationService] 🔍 Store check - Final match:', stringMatch || numberMatch);
    
    // ✅ CRITICAL: For multi-store sellers, only show notifications for the active store
    // Compare both as strings AND numbers for maximum compatibility
    // Only block if BOTH store IDs exist AND they DON'T match (as both string and number)
    // If activeStoreId is not set, allow all notifications (backward compatibility for single-store sellers)
    
    // ✅ ENHANCED: More lenient matching - if either string or number matches, allow it
    const storesMatch = stringMatch || numberMatch;
    const bypassStoreFilter = shouldBypassStoreNotificationFilter();
    
    if (bypassStoreFilter) {
      console.log('[NotificationService] Global notification role detected; bypassing active-store filter.');
    } else if (orderStoreIdRaw !== null && activeStoreIdRaw !== null) {
      // Both store IDs exist - check if they match (as string or number)
      if (!storesMatch) {
        console.log('[NotificationService] ⚠️ BLOCKED: Order is for a different store. Skipping notification.');
        console.log('[NotificationService] ⚠️ Order store:', orderStoreIdRaw, '(string:', orderStoreIdStr, ', number:', orderStoreId, ')');
        console.log('[NotificationService] ⚠️ Active store:', activeStoreIdRaw, '(string:', activeStoreIdStr, ', number:', activeStoreId, ')');
        console.log('[NotificationService] ⚠️ String match:', stringMatch, 'Number match:', numberMatch);
        return;
      } else {
        console.log('[NotificationService] ✅ ALLOWED: Order matches active store. Proceeding.');
        console.log('[NotificationService] ✅ Order store:', orderStoreIdRaw, 'Active store:', activeStoreIdRaw);
        console.log('[NotificationService] ✅ Match confirmed - proceeding with notification and sound');
      }
    } else if (activeStoreIdRaw === null) {
      // No active store ID set - allow all notifications (backward compatibility for single-store sellers)
      console.log('[NotificationService] ℹ️ ALLOWED: No active store ID set, allowing all notifications (backward compatibility)');
    } else if (orderStoreIdRaw === null) {
      // Order missing store_id - allow it (fallback for compatibility)
      console.log('[NotificationService] ℹ️ ALLOWED: Order missing store_id, allowing it (fallback for compatibility)');
    }
    
    // If no active store ID set, allow all notifications (backward compatibility)
    // If order missing store_id, allow it (fallback for compatibility)
    // Only block when explicitly different stores

    const orderId = (orderData.id || orderData.orderId)?.toString();
    const existingOrder = this.orderQueue.find(order =>
      (order.id || order.orderId)?.toString() === orderId
    );

    if (existingOrder) {
      console.log('Order already in queue, skipping duplicate:', orderId, 'Queue length:', this.orderQueue.length);
      return;
    }

    this.orderQueue.push(orderData);

    console.log('Order added to queue:', orderId, 'Queue length:', this.orderQueue.length);

    if (!this.isPlayingGlobalOrderSound) {
      // Start sound ONLY when it's not already playing.
      // Covers two cases:
      //   1. First order in an empty queue (queue length === 1)
      //   2. New order arrives AFTER previous 30s timeout expired
      // Sound will NOT restart from accept/reject/close/tab-switch — only from new FCM orders.
      // startGlobalOrderSound() handles audio init and AudioContext resume internally.
      this.startGlobalOrderSound();
    }
    // If sound is already playing, do NOT restart or reset the 30s timer.

    if (!this.isProcessingQueue) {
      this.showNextOrderInQueue();
    }
  }

  removeOrderFromQueue(orderId: string | number): void {
    const normalizedOrderId = orderId?.toString();

    if (!normalizedOrderId) {
      console.warn('removeOrderFromQueue called without a valid orderId');
      return;
    }

    const index = this.orderQueue.findIndex(order => {
      const currentId = (order.id ?? order.orderId)?.toString();
      return currentId === normalizedOrderId;
    });

    if (index === -1) {
      console.log('Order not found in queue, skipping removal:', normalizedOrderId);
      return;
    }

    const [removedOrder] = this.orderQueue.splice(index, 1);

    console.log(
      'Order removed from queue due to status update:',
      removedOrder.id || removedOrder.orderId,
      'Remaining:',
      this.orderQueue.length
    );

    const state = getStore().getState();
    const modalState = state.notification?.orderConfirmationModal;

    const isModalShowingRemovedOrder =
      modalState?.visible &&
      modalState?.orderData &&
      (
        modalState.orderData.id?.toString() === normalizedOrderId ||
        modalState.orderData.orderId?.toString() === normalizedOrderId
      );

    if (isModalShowingRemovedOrder) {
      getStore().dispatch(clearOrderConfirmationModal());
      this.isProcessingQueue = false;

      if (this.orderQueue.length > 0) {
        // Show next popup but do NOT restart sound.
        // Sound only plays once (30s) when new orders first arrive.
        this.showNextOrderInQueue();
      } else {
        this.stopAllSounds();
        this.navigateToOrdersOnLastOrder();
      }
      return;
    }

    if (this.orderQueue.length === 0) {
      this.isProcessingQueue = false;
      this.stopAllSounds();
      return;
    }

    if (index === 0 && this.isProcessingQueue) {
      this.showNextOrderInQueue();
    }
  }

  showNextOrderInQueue(): void {
    if (this.orderQueue.length > 0) {
      this.isProcessingQueue = true;
      const orderData = this.orderQueue[0];

      console.log('Showing order from queue:', orderData.id || orderData.orderId);

      getStore().dispatch(setOrderConfirmationModal({
        visible: true,
        orderData,
        confirmationType: 'pending',
        title: 'New Order Received',
        body: `Order #${orderData.id || orderData.orderId} from ${orderData.customer?.name || 'Customer'}`,
      }));
    }
  }

  processOrderQueue(): void {
    if (this.orderQueue.length > 0) {
      const orderData = this.orderQueue.shift()!;

      console.log('Processing order from queue:', orderData.id || orderData.orderId, 'Remaining:', this.orderQueue.length);

      if (this.orderQueue.length > 0) {
        this.showNextOrderInQueue();
      } else {
        this.isProcessingQueue = false;
        this.stopAllSounds();
      }
    } else {
      this.isProcessingQueue = false;
      this.stopAllSounds();
    }
  }

  acceptOrder(orderId: string | number): void {
    console.log('Order accepted:', orderId);

    this.removeOrderFromQueue(orderId);

    // Stop all sounds — do NOT restart even if queue has more orders.
    // Sound only plays once (30s) when new orders first arrive.
    this.stopAllSounds();

    // Notify other tabs to stop sound and remove this order
    this.broadcastOrderAction(orderId);

    window.dispatchEvent(new CustomEvent('orderAccepted', { detail: { orderId } }));
  }

  rejectOrder(orderId: string | number): void {
    console.log('Order rejected:', orderId);

    this.removeOrderFromQueue(orderId);

    // Stop all sounds — do NOT restart even if queue has more orders.
    // Sound only plays once (30s) when new orders first arrive.
    this.stopAllSounds();

    // Notify other tabs to stop sound and remove this order
    this.broadcastOrderAction(orderId);
  }

  private navigateToOrders(tab?: string): void {
    try {
      const state = getStore().getState();
      const storeId = localStorage.getItem('notification_store_id') || state.store?.storeID;
      localStorage.setItem('notification_store_id', storeId as string);
      // Priority order: profileStoreId[0]?.id > store.storeID > localStorage
      let currentStoreId: string | number | null = null;

      // Check store.storeID only if currentStoreId is not already set
      if (!currentStoreId && storeId && Number(storeId) > 0) {
        currentStoreId = storeId;
      } else {
        currentStoreId = localStorage.getItem('CURRENT_STORE_ID') || 0;
      }

      const tabHash = tab ? `#${tab}` : '#preparing';

      let ordersPath: string;
      if (currentStoreId && Number(currentStoreId) > 0) {
        ordersPath = `/stores/${currentStoreId}/orders${tabHash}`;
        console.log('Navigating to orders page (no reload):', ordersPath);
      } else {
        console.warn('Store ID not found, navigating to general orders page');
        ordersPath = `/dashboard/orders${tabHash}`;
        console.log('Navigating to general orders page (no reload):', ordersPath);
      }
      // Use custom event so React Router can navigate without full page reload
      window.dispatchEvent(new CustomEvent('orderQueueEmptyNavigate', { detail: { path: ordersPath } }));
    } catch (error) {
      console.error('Failed to navigate to orders page:', error);
      const tabHash = tab ? `#${tab}` : '#preparing';
      const ordersPath = `/dashboard/orders${tabHash}`;
      console.log('Error fallback - navigating to:', ordersPath);
      window.dispatchEvent(new CustomEvent('orderQueueEmptyNavigate', { detail: { path: ordersPath } }));
    }
  }

  closeOrderConfirmationModal(): void {
    console.log('Modal closed by user');
    // Stop all sounds immediately when modal is closed by user
    this.stopAllSounds();

    // Get current order ID before clearing modal, to broadcast to other tabs
    const state = getStore().getState();
    const modalData = state.notification?.orderConfirmationModal?.orderData;
    const currentOrderId = modalData?.id ?? modalData?.orderId;
    if (currentOrderId) {
      this.broadcastOrderAction(currentOrderId);
    }

    getStore().dispatch(clearOrderConfirmationModal());
    this.processCurrentOrderAndShowNext();
  }

  private processCurrentOrderAndShowNext(): void {
    if (this.orderQueue.length > 0) {
      const processedOrder = this.orderQueue.shift()!;
      console.log('Processed order:', processedOrder.id || processedOrder.orderId, 'Remaining:', this.orderQueue.length);

      if (this.orderQueue.length > 0) {
        // Show next popup but do NOT restart sound.
        // Sound only plays once (30s) when new orders first arrive.
        this.showNextOrderInQueue();
      } else {
        this.isProcessingQueue = false;
        this.stopAllSounds();
        this.navigateToOrdersOnLastOrder();
      }
    } else {
      this.isProcessingQueue = false;
      this.stopAllSounds();
      this.navigateToOrdersOnLastOrder();
    }
  }

  getUnreadCount(): number {
    const state = getStore().getState();
    return state.notification?.unreadCount || 0;
  }

  getQueueStatus(): { length: number; orders: string[] } {
    return {
      length: this.orderQueue.length,
      orders: this.orderQueue.map(order => order.orderId || 0)
    };
  }

  logQueueStatus(): void {
    const status = this.getQueueStatus();
    console.log('Queue status - Length:', status.length, 'Orders:', status.orders, 'Processing:', this.isProcessingQueue, 'Sound:', this.isPlayingGlobalOrderSound);
  }

  private startQueueMonitoring(): void {
    setInterval(() => {
      if (this.orderQueue.length > 0) {
        console.log('Queue monitor - Active orders:', this.orderQueue.length, 'Sound:', this.isPlayingGlobalOrderSound);
      }
    }, 30000);
  }

  /** Public API for UI to trigger audio init on user gesture (e.g. "Enable Sound" button). */
  async tryInitializeAudio(): Promise<void> {
    return this.initializeAudio();
  }

  isAudioReady(): boolean {
    return this.audioInitialized && !!this.audioContext && !!this.notificationSound;
  }

  getAudioStatus(): {
    initialized: boolean;
    context: boolean;
    contextState?: string;
    sound: boolean;
    ready: boolean;
    lastError: string | null;
    lastPlayedAt: string | null;
  } {
    return {
      initialized: this.audioInitialized,
      context: !!this.audioContext,
      contextState: this.audioContext?.state,
      sound: !!this.notificationSound,
      ready: this.isAudioReady(),
      lastError: this.lastSoundError,
      lastPlayedAt: this.lastSoundPlayedAt,
    };
  }

  getNotifications(): InAppNotification[] {
    const state = getStore().getState();
    return state.notification?.inAppNotifications || [];
  }

  clearAllNotifications(): void {
    getStore().dispatch(clearAllNotifications());
  }

  getCurrentStatus(): void {
    console.log('Status - Queue:', this.orderQueue.length, 'Processing:', this.isProcessingQueue, 'Sound:', this.isPlayingGlobalOrderSound, 'Audio:', this.audioInitialized);
  }

  manualStartSound(): void {
    this.startGlobalOrderSound();
  }

  manualStopSound(): void {
    this.stopGlobalOrderSound();
  }

  setStoreId(storeId: number): void {
    console.log('Setting store ID to:', storeId);
    getStore().dispatch({ type: 'store/setStoreID', payload: storeId });
    localStorage.setItem('CURRENT_STORE_ID', storeId.toString());
    localStorage.setItem('storeID', storeId.toString());
    console.log('Store ID set successfully');
  }

  forceRestartSound(): void {
    if (this.orderQueue.length > 0 && !this.isPlayingGlobalOrderSound) {
      console.log('Queue has orders but sound is not playing - restarting...');
      this.startGlobalOrderSound();
    } else if (this.orderQueue.length === 0) {
      console.log('Queue is empty - no need to restart sound');
    } else {
      console.log('Sound is already playing - no action needed');
    }
  }

   /** True if any repeating order sound is currently playing. */
   isAnyOrderSoundPlaying(): boolean {
     return this.isPlayingGlobalOrderSound || this.isPlayingContinuousSound;
   }

  private navigateToOrdersOnLastOrder(): void {
    console.log('All orders processed, navigating to Preparing tab');
    setTimeout(() => {
      if (this.orderQueue.length === 0) {
        console.log('Queue confirmed empty, navigating to Preparing tab');
        this.navigateToOrders('preparing');
      } else {
        console.log('Queue not empty, skipping navigation. Queue length:', this.orderQueue.length);
      }
    }, 100);
  }

}

export const notificationService: NotificationService = new NotificationService();
